import React, { ReactElement, useMemo } from 'react';
import AnalyticsOverview from './overview';
import AnalyticsMonetization from './monetization';
import SecondaryNavigation from '../common/navigation/secondary-navigation';
import classnames from 'classnames';
import { SigmaContainer } from '@host-panel/analytics/sigma/SigmaContainer';
import useSpotConfig from '@host-panel/request/config/use-config';
import { AnalyticsSecondaryTab, AnalyticsSecondaryTabItem } from '@host-panel/analytics/types';
import { PageRoute } from '../../configs/routeConstants';
import { NoPermission } from '../common/auth-checker/NoPermission';

const ANALYTICS_TABS = 'tenant.admin-panel.analytics_tabs';

type AnalyticsProps = {
  spotId: string;
  page: string;
  isSuperAdmin?: boolean;
  enableMonetization: boolean;
  ssoEnabled: boolean;
  isAuthorized?: boolean;
};

export const Analytics = ({
  spotId,
  page,
  isSuperAdmin,
  enableMonetization,
  ssoEnabled,
  isAuthorized
}: AnalyticsProps): ReactElement => {
  const [[analyticsTabs]] = (useSpotConfig([ANALYTICS_TABS], {
    [ANALYTICS_TABS]: [AnalyticsSecondaryTab.Overview, AnalyticsSecondaryTab.Monetization]
  }) as unknown) as [[Array<AnalyticsSecondaryTab>]];

  const showOverview = analyticsTabs.includes(AnalyticsSecondaryTab.Overview);
  const showEngagement = analyticsTabs.includes(AnalyticsSecondaryTab.Engagement);
  const showRevenue = analyticsTabs.includes(AnalyticsSecondaryTab.Revenue);
  const showModeration = analyticsTabs.includes(AnalyticsSecondaryTab.Moderation) || isSuperAdmin;

  const showMonetization =
    analyticsTabs.includes(AnalyticsSecondaryTab.Monetization) &&
    (isSuperAdmin || enableMonetization);

  const secondaryMenuItems = useMemo(() => {
    const navParams = { spot_id: spotId };
    const rawItems: Array<{
      visible: boolean;
      item: AnalyticsSecondaryTabItem;
    }> = [
      {
        visible: showOverview,
        item: {
          label: 'Overview',
          routeName: 'analytics-overview',
          navParams
        }
      },
      {
        visible: showMonetization,
        item: {
          label: 'Monetization',
          routeName: 'analytics-monetization',
          navParams
        }
      },
      {
        visible: showEngagement,
        item: {
          label: 'Engagement',
          routeName: PageRoute.AnalyticsEmbedEngagement,
          navParams
        }
      },
      {
        visible: showRevenue,
        item: {
          label: 'Revenue',
          routeName: PageRoute.AnalyticsEmbedRevenue,
          navParams
        }
      },
      {
        visible: showModeration,
        item: {
          label: 'Moderation',
          routeName: PageRoute.AnalyticsEmbedModeration,
          navParams
        }
      }
    ];

    return rawItems.filter(({ visible }) => visible).map(({ item }) => item);
  }, [spotId, showEngagement, showMonetization, showOverview, showRevenue, showModeration]);

  if (!isAuthorized) {
    return <NoPermission />;
  }

  return (
    <div className="preferences">
      <SecondaryNavigation
        title="Analytics"
        selected={page}
        menuItems={secondaryMenuItems}
        className={classnames('analytics-embed', 'secondary-navigation')}
      />
      {page === 'analytics-overview' && showOverview && (
        <AnalyticsOverview spotId={spotId} ssoEnabled={ssoEnabled} />
      )}
      {page === 'analytics-monetization' && showMonetization && (
        <AnalyticsMonetization spotId={spotId} />
      )}
      {page === PageRoute.AnalyticsEmbedEngagement && showEngagement && (
        <SigmaContainer page={page} />
      )}
      {page === PageRoute.AnalyticsEmbedRevenue && showRevenue && <SigmaContainer page={page} />}
      {page === PageRoute.AnalyticsEmbedModeration && showModeration && (
        <SigmaContainer page={page} />
      )}
    </div>
  );
};
